import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, PenLine, Quote } from "lucide-react";
import { Footer } from "../components/Footer";

const NOTES_URL =
  "https://www.icloud.com/notes/069uCxWbNlf6KSgg25PzjLmLw#Journey_Notes";

const PARAGRAPHS = [
  "I am a writer, and these are my notes. They represent many things and all of them are a part of me, of my journey since around 2019. Through them, you will get to know me in ways I cannot be known otherwise. Romantic, in love, furious, sad. Joyful, lost, desperate, everything. Texts written in a hurry, in train stations, airports, deserted streets, or dark nights. Moments when it was just me and the words. You will see me bare, in many moments—from the darkest ones to those most filled with presence and joy. Yet, all of them speak of a real path and an authentic story. You will see me holding a multitude of emotions that I didn't know what to do with, other than write them down.",
  "Sometimes, they are conversations with myself; other times, conversations with the universe or with people. Sometimes, they were things I couldn't express in any other way than through writing. Other times, thoughts racing through my mind. Sometimes, I transposed an emotion into words, bringing it out from deep within me, here, through language. Other times, I discharged energy through them. Sometimes they were therapy, other times the only thing reminding me of who I am. They all carry an energy within them. Please, when you read them, be present and feel it. That is the only way you will truly be able to understand them. I never thought I would share them with the world—not like this, so vulnerably. But I find an extraordinary wisdom and beauty in them, and if once this used to be my place, mine alone, today I feel in my heart that I must do this and let you in as well.",
  "Sometimes, there will also be things that I didn't write myself, but they will be marked with \"\" (quotation marks). They are words that touched deep parts of me and stayed with me. So, I placed them among my own—just remember the quotation marks.",
];

export default function RawNotes() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="raw-notes-page"
      className="relative z-10 min-h-screen"
      style={{
        background:
          "radial-gradient(1100px 550px at 30% -10%, rgba(214,181,109,0.16), transparent 65%), radial-gradient(900px 500px at 90% 100%, rgba(122,158,109,0.10), transparent 60%), #06040A",
      }}
    >
      <header className="sticky top-0 z-50 bg-[#06040A]/80 backdrop-blur-xl border-b border-[#2C223A]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="notes-logo">
            <img
              src="/ovidiu-logo.png"
              alt="Ovidiu"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="notes-back-home"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-[#A59DAB] hover:text-[#D6B56D] transition-colors"
          >
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-20 md:pt-28 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-3 mb-7 text-[#D6B56D]">
            <PenLine size={16} />
            <span className="text-xs font-bold uppercase tracking-[0.35em]">
              Raw notes
            </span>
            <PenLine size={16} />
          </div>
          <h1 className="font-heading font-light text-5xl md:text-7xl text-[#EBE4D8] leading-[1.02]">
            My notes<span className="text-[#D6B56D]">...</span>
            <span className="italic text-[#D6B56D]"> unfiltered words.</span>
          </h1>
        </motion.div>

        {/* Hero image — sets the introspective tone */}
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.25 }}
          data-testid="notes-hero-image"
          className="relative mt-14 md:mt-20 overflow-hidden rounded-3xl border border-[#2C223A]"
        >
          <img
            src="/raw-notes-hero.jpg"
            alt="Alone by the sea"
            className="block w-full h-auto"
          />
        </motion.figure>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.85, delay: 0.15 }}
          className="mt-16 space-y-6"
        >
          {PARAGRAPHS.map((p, i) => (
            <p
              key={i}
              data-testid={`notes-paragraph-${i}`}
              className="text-[#C8C0BE] leading-[1.85] text-base md:text-lg font-light"
            >
              {p}
            </p>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-14 flex items-center gap-4 text-[#A59DAB]/70 italic"
        >
          <Quote size={18} className="text-[#D6B56D]" />
          <p className="text-sm md:text-base font-light">
            Read slowly. Read with presence.
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.21 }}
          data-testid="notes-order-note"
          className="mt-10 text-sm text-[#A59DAB]/80 leading-relaxed font-light italic border-l-2 border-[#D6B56D]/40 pl-4"
        >
          <span className="not-italic uppercase tracking-[0.25em] text-[11px] text-[#D6B56D] block mb-1.5">First note</span>
          The notes are in chronological order. The first ones are from around
          2020 — written by the oldest version of me — and the last ones are
          from the present. Reading them in order is like walking my growth
          beside me.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.22 }}
          data-testid="notes-icloud-note"
          className="mt-6 text-sm text-[#A59DAB]/80 leading-relaxed font-light italic border-l-2 border-[#D6B56D]/40 pl-4"
        >
          <span className="not-italic uppercase tracking-[0.25em] text-[11px] text-[#D6B56D] block mb-1.5">Second note</span>
          The link opens in iCloud and it may ask you to sign in or create a
          free iCloud account before you can read the notes. You don't need to
          own an iPhone — any device with a browser works.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-20 flex flex-col items-center gap-6"
        >
          <a
            href={NOTES_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="notes-open-link"
            className="group relative inline-flex items-center gap-3 px-10 py-4 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all duration-300 shadow-[0_20px_60px_-20px_rgba(214,181,109,0.55)]"
          >
            Open the notes
            <ExternalLink
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5"
            />
          </a>
          <p className="text-xs text-[#A59DAB]/70 font-light tracking-wide">
            Opens iCloud in a new tab
          </p>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
